import { useState } from 'react';
import {
    Box,
    Button,
    Heading,
    HStack,
    Spacer,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FaIcon } from '@acp/core-ui';
import { formatDistanceToNow, parseISO } from 'date-fns';
import { faCircle, faCircleCheck } from '@fortawesome/pro-solid-svg-icons';
import { UserScopedNotification } from '@acp/shared';
import { useNotificationItem } from '../hooks/use-notification-item';

function UnreadMarkerIcon({ isRead }: { isRead: boolean }) {
    return (
        <Box w={2} flexShrink={0}>
            {isRead === false && (
                <FaIcon
                    icon={faCircle}
                    color="info.500"
                    fontSize={8}
                    position="absolute"
                    top={6}
                    left={4}
                />
            )}
        </Box>
    );
}

export function NotificationItem({ notification }: { notification: UserScopedNotification }) {
    const relativeDate = formatDistanceToNow(
        parseISO(notification.notifiedAt),
        { addSuffix: true }
    );

    const {
        onClick,
        onMarkAsReadClick,
        getNotifiedAt,
    } = useNotificationItem(notification);

    const notifiedAt = getNotifiedAt();

    const [markedAsRead, setMarkedAsRead] = useState(false);

    const handleMarkAsReadClick = () => {
        setMarkedAsRead(true);
        onMarkAsReadClick();
    };

    return (
        <HStack
            spacing={3}
            p={4}
            w="full"
            borderBottomWidth={1}
            borderBottomColor="muted.50"
            _hover={{
                backgroundColor: 'blackAlpha.50',
            }}
            onClick={() => {
                setMarkedAsRead(true);
                onClick();
            }}
            cursor="pointer"
            position="relative"
        >
            <UnreadMarkerIcon isRead={notification.isRead} />

            <VStack w="full" align="stretch" spacing={2}>
                <VStack align="flex-start" spacing={2}>
                    {notification.priority === 'HIGH' && (
                        <Heading
                            fontSize="0.8em"
                            color="danger.500"
                            letterSpacing={1.7}
                            fontWeight="bold"
                        >
                            PRIORITY
                        </Heading>
                    )}

                    <Text fontWeight="medium">{notification.title}</Text>
                    <Text dangerouslySetInnerHTML={{ __html: notification.details }} />

                    <HStack spacing={2} fontSize="sm" color="muted.500" w="full">
                        <Text
                            _hover={{
                                _after: {
                                    content: notifiedAt,
                                },
                            }}
                        >
                            {relativeDate}
                        </Text>

                        <Spacer />

                        {markedAsRead && (
                            <FaIcon icon={faCircleCheck} color="success.500" fontSize="md" />
                        )}

                        {notification.isRead === false && (
                            <Button
                                as={Text}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsReadClick();
                                }}
                                w="fit-content"
                                h="auto"
                                variant="unstyled"
                                fontSize="inherit"
                                fontWeight="normal"
                                _hover={{
                                    textDecor: 'underline',
                                }}
                                cursor="pointer"
                                color="charcoal.500"
                            >
                                Mark as read
                            </Button>
                        )}
                    </HStack>
                </VStack>
            </VStack>
        </HStack>
    );
}
